import { useEffect, useRef, useState } from "react";
import { RotateCw, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

/**
 * Premium bag viewer with 360° pseudo-rotation, zoom, and CSS-filter tinting.
 * The bag image is cropped from the user's reference sprite (top-left cell = Klein Blue),
 * then tinted with the selected color's CSS filter.
 */
export const BagViewer = ({ color, onLaunchAR, autoSpin = true }) => {
  const [angle, setAngle] = useState(-8);
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef({ x: 0, angle: 0 });
  const spinRef = useRef(null);

  // Subtle auto-spin when idle
  useEffect(() => {
    if (!autoSpin || dragging) return;
    spinRef.current = setInterval(() => {
      setAngle((a) => a + 0.15);
    }, 40);
    return () => clearInterval(spinRef.current);
  }, [autoSpin, dragging]);

  const onPointerDown = (e) => {
    setDragging(true);
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    startRef.current = { x, angle };
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const dx = x - startRef.current.x;
    setAngle(startRef.current.angle + dx * 0.6);
  };
  const onPointerUp = () => setDragging(false);

  return (
    <div
      data-testid="bag-viewer"
      className="relative w-full select-none"
      style={{ touchAction: "none" }}
    >
      <div
        className="relative aspect-square w-full overflow-hidden bg-[var(--kb-bg-2)] kb-grain"
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        onTouchEnd={onPointerUp}
      >
        {/* Indexed corner labels (editorial detail) */}
        <div className="absolute top-4 left-4 kb-mono-label text-[var(--kb-fg-2)] z-10">
          [{String(color.id).slice(0, 3).toUpperCase()}] · 360°
        </div>
        <div className="absolute top-4 right-4 kb-mono-label text-[var(--kb-fg-2)] z-10">
          ZOOM · {(zoom * 100).toFixed(0)}%
        </div>

        {/* Bag */}
        <div
          data-testid="bag-image"
          className="kb-bag absolute inset-6 sm:inset-10"
          style={{
            "--kb-tint": color.filter,
            transform: `perspective(1200px) rotateY(${angle}deg) scale(${zoom})`,
            transition: dragging ? "none" : "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />

        {/* Floor shadow */}
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-6 sm:bottom-10 w-2/3 h-3 rounded-full"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(10,10,10,0.18) 0%, rgba(10,10,10,0) 70%)",
            filter: "blur(2px)",
          }}
        />

        <div className="absolute bottom-4 left-4 kb-mono-label text-[var(--kb-fg-2)] z-10">
          DRAG TO ROTATE
        </div>
        <div className="absolute bottom-4 right-4 kb-mono-label kb-blue z-10">
          {color.name}
        </div>
      </div>

      {/* Controls */}
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            data-testid="bag-zoom-out"
            onClick={() => setZoom((z) => Math.max(0.7, z - 0.1))}
            className="kb-btn-ghost !p-3"
            aria-label="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <button
            data-testid="bag-zoom-in"
            onClick={() => setZoom((z) => Math.min(1.6, z + 0.1))}
            className="kb-btn-ghost !p-3"
            aria-label="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
          <button
            data-testid="bag-spin"
            onClick={() => setAngle((a) => a + 360)}
            className="kb-btn-ghost !p-3"
            aria-label="Spin 360"
          >
            <RotateCw size={16} />
          </button>
        </div>
        <button
          data-testid="bag-launch-ar"
          onClick={onLaunchAR}
          className="kb-btn-primary"
        >
          <Maximize2 size={14} /> View in AR
        </button>
      </div>
    </div>
  );
};
